import { NotFlexSpacer } from "Bands/containers/EventSchedule/styles";
import React, { createRef, useMemo } from "react";
import {
  IActionDropdownItem,
  ImageGrid,
  Loader,
  Pager,
  Title,
} from "shared/components";
import { GridContainer } from "shared/styles";
import { IInfoBlockItem, IInfoBlockItemType, IPager } from "shared/types";
import { getSortedInfoBlockItems } from "shared/utils";
import {
  InfoInnerTitleWrapper,
  StyledImageGridWrapper,
  StyledInfoComponentWrapper,
} from "./style";

export interface InfoBlockItemsProps {
  title?: string;
  type: IInfoBlockItem;
  items: IInfoBlockItemType[];
  Component: React.ComponentType<any>;
  getItemActions?: (id: string, type: IInfoBlockItem) => IActionDropdownItem[];
  pager?: IPager;
  loading?: boolean;
  chooseItem?: (item: IInfoBlockItemType) => void;
  disabledItems?: IInfoBlockItemType[];
  componentProps?: { [key: string]: any };
  breadcrumbs?: string | string[];
  sortItems?: boolean;
}

export const InfoBlockItems: React.FC<InfoBlockItemsProps> = ({
  title,
  type,
  items,
  Component,
  getItemActions,
  pager,
  loading,
  chooseItem,
  disabledItems,
  componentProps,
  breadcrumbs,
  sortItems,
}) => {
  const sortedItems = useMemo(
    () => (sortItems ? getSortedInfoBlockItems(items, type) : items),
    [items, type, sortItems]
  );
  const scrollRef = createRef<HTMLDivElement>();

  const handlePageChange = (page: number) => {
    // scroll to top of list
    scrollRef.current?.scrollIntoView({
      behavior: "smooth",
    });

    pager?.onPageChange(page);
  };

  const itemsWithActions = useMemo(() => {
    const disabledItemIds = disabledItems?.map((item) => item.id);

    const getButtonItemProps = (item: IInfoBlockItemType) => ({
      onClick: () => chooseItem && chooseItem(item),
      disabled: disabledItemIds?.includes(item.id),
    });

    return sortedItems.map((item: IInfoBlockItemType) => ({
      ...item,
      ...componentProps,
      actions: getItemActions && item?.id ? getItemActions(item?.id, type) : [],
      ...(chooseItem && getButtonItemProps(item)),
    }));
  }, [
    sortedItems,
    getItemActions,
    type,
    componentProps,
    chooseItem,
    disabledItems,
  ]);

  return (
    <NotFlexSpacer size="s">
      {title && (
        <InfoInnerTitleWrapper>
          <Title asComponent="h3" title={title} size="m" />
        </InfoInnerTitleWrapper>
      )}
      {loading && <Loader />}
      {!loading && !!itemsWithActions.length && (
        <>
          {type === IInfoBlockItem.image ? (
            <StyledImageGridWrapper ref={scrollRef} data-scroll-ref>
              <ImageGrid
                breadcrumbs={breadcrumbs}
                images={itemsWithActions}
                maxNbRows={getItemActions ? undefined : 1}
              />
            </StyledImageGridWrapper>
          ) : (
            <div ref={scrollRef} data-scroll-ref>
              <StyledInfoComponentWrapper>
                <GridContainer>
                  {itemsWithActions?.map((item: IInfoBlockItemType) => (
                    <Component key={item.id} {...item} />
                  ))}
                </GridContainer>
              </StyledInfoComponentWrapper>
            </div>
          )}
        </>
      )}
      {pager && pager.currentPage && (
        <Pager {...pager} onPageChange={handlePageChange} />
      )}
    </NotFlexSpacer>
  );
};
